import { IonButton, IonInput } from '@ionic/react';
import React, { KeyboardEvent, useRef, useState } from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import { Icon } from '@/components/memo';
import { useAddDentist } from '@/contexts';
import { useInputFocus, useToast } from '@/hooks';
import { ADD_DENTIST_STEPS } from '@/utils/constants';
import { arrowForwardCircleOutline } from 'ionicons/icons';

import './Name.css';

interface NameProps {}

const Name: React.FC<NameProps> = () => {
  const { firstName, lastName, setFirstName, setLastName, setCurrentStep } =
    useAddDentist();
  const displayToast = useToast();

  const [hasFirstNameError, setHasFirstNameError] = useState<boolean>(false);
  const [hasLastNameError, setHasLastNameError] = useState<boolean>(false);

  const inputRef = useRef<HTMLIonInputElement>(null);

  useInputFocus(inputRef);

  const copy = {
    subHeader: 'DENTIST NAME',
    header: (
      <>
        What's your <span>name</span>?
      </>
    ),
    buttonText: 'Next'
  };

  const onFirstNameInput = (event: CustomEvent) => {
    setFirstName(event.detail.value!);
    setHasFirstNameError(false);
  };

  const onLastNameInput = (event: CustomEvent) => {
    setLastName(event.detail.value!);
    setHasLastNameError(false);
  };

  const onNext = () => {
    let hasError = false;
    if (!firstName || firstName.length <= 1) {
      setHasFirstNameError(true);
      hasError = true;
    }
    if (!lastName || lastName.length <= 1) {
      setHasLastNameError(true);
      hasError = true;
    }
    if (hasError) {
      displayToast({
        message:
          'Please enter a valid first and last name. It should be at least 2 characters.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    } else {
      setHasFirstNameError(false);
      setHasLastNameError(false);
      setCurrentStep(ADD_DENTIST_STEPS.HOURS);
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNext();
    }
  };

  return (
    <div className="name">
      <Spacer top={32} bottom={24}>
        <h4 className="name__sub-header">{copy.subHeader}</h4>
        <h1 className="name__header">{copy.header}</h1>
      </Spacer>

      <Spacer bottom={102}>
        <div className="name__form">
          <div
            className={`name__input ${hasFirstNameError ? 'name__input--error' : ''}`}
          >
            <IonInput
              ref={inputRef}
              label="First Name"
              labelPlacement="stacked"
              value={firstName}
              placeholder="Enter your first name"
              onIonInput={onFirstNameInput}
              onKeyDown={onKeyDown}
            />
          </div>
          <div
            className={`name__input ${hasLastNameError ? 'name__input--error' : ''}`}
          >
            <IonInput
              label="Last Name"
              labelPlacement="stacked"
              value={lastName}
              placeholder="Enter your last name"
              onIonInput={onLastNameInput}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>
      </Spacer>
      <ButtonContainer>
        <IonButton
          id="next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <Icon slot="start" icon={arrowForwardCircleOutline} />
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default Name;
